import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Heading,
  Message,
  PageContainer,
  Paragraph,
  spacing,
  Spinner,
  PageContainerWrapper,
  Modal,
} from 'component-library';
import { type CheckoutProps } from './types';
import {
  CheckoutWrapper,
  DesktopHeaderContainer,
  FormWrapper,
  MobileHeaderContainer,
  StyledContainer,
  SummaryWrapper,
} from './styles';
import { CheckoutForm } from './CheckoutForm';
import OrderSummary from './OrderSummary';

export const Checkout: React.FC<CheckoutProps> = ({
  subtitle,
  subscriptionType,
  country,
  apiHooks,
  onCheckout,
  isAllowedToPurchase,
  s3AssetsUrl,
  loading = false,
  error,
  onCountryChange,
  onHierarchyChange,
  hasEnterprisePartner,
  isCheckingEnterprise,
  countryPicker,
  reset,
  hideTitle = false,
}) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState<boolean>(false);

  if (loading) {
    return (
      <PageContainer>
        <Spinner size="large" />
      </PageContainer>
    );
  }

  return (
    <>
      {!isAllowedToPurchase && (
        <PageContainer>
          <Message
            severity="warning"
            title={t('no_permission.title', 'No permission')}
            description={t(
              'no_permission.description',
              'You do not have permission to purchase subscriptions',
            )}
          />
        </PageContainer>
      )}
      <StyledContainer>
        <PageContainerWrapper style={{ padding: '0' }}>
          {isAllowedToPurchase ? (
            <CheckoutWrapper data-testid="subscription-checkout">
              <MobileHeaderContainer>
                <Heading variant="xLarge" as="h1" mb={spacing.xsmall}>
                  {t('subscription.checkout.title')}
                </Heading>
                <Heading variant="small" as="h2" mb={countryPicker ? spacing.small : undefined}>
                  {subtitle}
                </Heading>
                {countryPicker && <div>{countryPicker}</div>}
              </MobileHeaderContainer>
              <FormWrapper>
                <DesktopHeaderContainer>
                  <div>
                    <Heading variant="xLarge" as="h1" mb={spacing.xsmall}>
                      {t('subscription.checkout.title')}
                    </Heading>
                    <Heading variant="small" as="h2" mb={spacing.mediumHigh}>
                      {subtitle}
                    </Heading>
                  </div>
                  {countryPicker && <div>{countryPicker}</div>}
                </DesktopHeaderContainer>
                <CheckoutForm
                  submitOrder={onCheckout}
                  country={country}
                  subscriptionType={subscriptionType}
                  apiHooks={apiHooks}
                  s3AssetsUrl={s3AssetsUrl}
                  onCountryChange={onCountryChange}
                  onHierarchyChange={onHierarchyChange}
                  hasEnterprisePartner={hasEnterprisePartner}
                  isCheckingEnterprise={isCheckingEnterprise}
                  reset={reset}
                  hideTitle={hideTitle}
                />
                {error ? (
                  <div style={{ margin: `${spacing.mediumLow} 0 ${spacing.mediumLow} 0` }}>
                    <Message
                      severity="error"
                      title={t('subscription.error.title')}
                      description={t('subscription.error.text')}
                    />
                  </div>
                ) : null}
              </FormWrapper>
              <SummaryWrapper
                data-testid={`subscription-summary-${subscriptionType.toLowerCase()}`}
              >
                <OrderSummary country={country} type={subscriptionType} />
              </SummaryWrapper>
            </CheckoutWrapper>
          ) : null}
        </PageContainerWrapper>
      </StyledContainer>
      <Modal
        open={showModal}
        onClose={() => {
          setShowModal(false);
        }}
        primaryAction={{
          label: t('confirm.cancel'),
          action: () => {
            window.history.back();
          },
        }}
      >
        <Heading as="h2" variant="medium" mb={spacing.small}>
          {t('cancel.checkout')}
        </Heading>
        <Paragraph variant="large">{t('cancel.checkout.text')}</Paragraph>
      </Modal>
    </>
  );
};

export { Checkout as default };
